import { useCallback, useEffect, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";

// #2813 (scribe-resilience workstream, docs/score/scribe-resilience.md gap
// G6, layer 7) — the generalised review queue. Was: every org ride still
// flagged needsReview (score-api GET /ride?needsReview=true, #387/#388). Now:
// score-api GET /review-queue, which merges needsReview rides AND unreviewed
// quarantine rows (#2812) into one item shape (subject/reason/decision/
// options/provenance/resolution) — see handlers/review-queue.js. Org-wide,
// not show-scoped, same posture the old queue always had (a manual entry can
// happen on any show/day).
//
// #2121 — this used to be mounted by AdminShell on the VERY FIRST authenticated
// screen alongside the show load (since #3496 the nav badge reads the server's
// badge-counts call instead; only the Review Queue page and the attach dialog
// mount this now). The class resolution below still must not fan out
// one GET /class/:classId per distinct class (that used to run a
// Promise.all of N requests in the same breath as the show load). Instead
// it derives the distinct showIds off the queue's RIDE-subject items and
// makes ONE POST /class/batch call (score-api's listClassesForShows, #1961),
// already bounded server-side by MAX_BATCH_IDS + queryEachInChunks (#2024).
// Mirrors scribe-fe's useScribeAssignments (#1961), which batches the exact
// same way.
//
// #2127 — a ride subject's showId is not a safe key for every row (a manual
// ride can be created with no showId at all, ride.js's createManualRide
// comment). Those rows' classId would never appear in the showId-batched
// call above, so any miss gets a per-miss GET /class/:classId fallback
// below, bounded to just the misses.
//
// #3413 — `showId` narrows the queue to that show (the server also keeps items
// whose show cannot be resolved, never dropping them). Omit it for the
// org-wide queue. `ready: false` holds the first fetch back while the active
// show is still loading, so the page never flashes the org-wide list first.
export function useReviewQueue({ showId, ready = true } = {}) {
  const scoreApi = useScoreApi();
  const [state, setState] = useState({ items: [], loading: true, error: null });

  // #2821 (docs/score/scribe-resilience.md gap G9, invariant I8) — a failed
  // refetch (network drop, a stale/expired session, a slow connection that
  // times out) must never wipe the list this hook is already showing. Before
  // this fix the catch below unconditionally set `items: []`, so an operator
  // looking at a populated review queue when their connection blipped would
  // see the whole page go empty rather than keep the last-known items with an
  // error banner layered on top (ReviewQueue.jsx already renders `error`
  // alongside the table, not instead of it). There is no IndexedDB cache to
  // paint from here (scoreadmin-fe's review queue is online-only, org-wide
  // data), so "the cache" is simply whatever this hook already resolved.
  const refetch = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const items = unwrap(await scoreApi.reviewQueue.listReviewQueue(showId || undefined));
      const rideItems = items.filter((item) => item.subject?.type === "ride");

      // #864 — resolve full class objects (not just a name) so the queue can
      // render classNumberLabel() via ClassLink; a row whose class never
      // resolves degrades to ReviewQueue.jsx's ClassLink `fallback` prop.
      const showIds = [...new Set(rideItems.map((item) => item.subject.showId).filter(Boolean))];
      let classById = {};
      if (showIds.length) {
        try {
          const classes = unwrap(await scoreApi.classes.listClassesForShows({ showIds }));
          classById = Object.fromEntries(classes.map((c) => [c.id, c]));
        } catch {
          // A failed batch lookup leaves classById empty here; the per-miss
          // fallback below then tries every row's classId individually.
          classById = {};
        }
      }
      const missingClassIds = [
        ...new Set(
          rideItems.map((item) => item.subject.classId).filter((id) => id && !classById[id])
        ),
      ];
      if (missingClassIds.length) {
        const entries = await Promise.all(
          missingClassIds.map((id) =>
            scoreApi.classes
              .getClass(id)
              .then(unwrap)
              .then((c) => [id, c])
              .catch(() => [id, null]),
          ),
        );
        for (const [id, c] of entries) {
          if (c) classById[id] = c;
        }
      }

      const enrichedItems = items.map((item) =>
        item.subject?.type === "ride"
          ? { ...item, subject: { ...item.subject, class: classById[item.subject.classId] ?? null } }
          : item,
      );
      setState({ items: enrichedItems, loading: false, error: null });
    } catch (err) {
      // #2821 — keep showing whatever items are already on screen; only the
      // error and loading flags change. A cold-mount failure (no items yet)
      // still settles to an empty list here, which is unchanged from before.
      setState((s) => ({ items: s.items, loading: false, error: err }));
    }
  }, [scoreApi, showId]);

  useEffect(() => {
    if (ready) refetch();
  }, [refetch, ready]);

  // #2813 AC4 — resolving an item goes through the generic endpoint; the
  // server records who (the caller's JWT sub) and what (action/subjectId) as
  // an audit event. Ride items' "attach" action does NOT go through here —
  // it stays on the existing AttachRideDialog -> resolveManualRide flow,
  // which needs an operator-chosen target ride and the test-mismatch guard.
  const resolveItem = useCallback(
    async (itemId, action, note = null) => {
      const result = unwrap(await scoreApi.reviewQueue.resolveReviewItem(itemId, { action, note }));
      await refetch();
      // #3534 - the server's answer (a test-change Switch can say overrideWon).
      return result;
    },
    [scoreApi, refetch],
  );

  return { ...state, refetch, resolveItem };
}

export default useReviewQueue;
